import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Card, Modal, Badge } from "./ui";
import { LANGS } from "../i18n";
import { FilePlus2, Pencil, Trash2, Eye, EyeOff, Plus, BookOpen, Newspaper, Sparkles, Loader2, Send, MailCheck, History } from "lucide-react";

const ta = "w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]";

function Field({ label, children }) {
  return <label className="block w-full"><span className="mb-1 block text-xs font-medium opacity-70">{label}</span>{children}</label>;
}

export default function ContentManager() {
  const [blog, setBlog] = useState([]);
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null); // { kind, origSlug, item }
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [previewLang, setPreviewLang] = useState("ENG");
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState("");
  const [beEmail, setBeEmail] = useState("");
  const [beSlugs, setBeSlugs] = useState([]);
  const [beBusy, setBeBusy] = useState(false);
  const toggleSlug = (slug) => setBeSlugs((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  const sendBackIssues = async () => {
    const em = beEmail.trim();
    if (!em || beSlugs.length === 0) { setMsg("Enter a subscriber email and select at least one issue."); return; }
    setBeBusy(true);
    try {
      const { data } = await client.post("/site/admin/newsletter/send-issues", { email: em, slugs: beSlugs });
      setMsg(`Sent ${data.sent.length} issue(s) to ${em}${data.failed.length ? `, ${data.failed.length} failed` : ""}.`);
      setBeSlugs([]);
    } catch (e) { setMsg(apiErr(e)); }
    finally { setBeBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [b, n] = await Promise.all([client.get("/site/admin/blog"), client.get("/site/admin/newsletter")]);
      setBlog(b.data); setNews(n.data);
    } catch (e) { setMsg(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const draftNext = async (kind) => {
    setBusy(true); setMsg("");
    try {
      const { data } = await client.post(`/site/admin/${kind === "blog" ? "blog" : "newsletter"}/draft-next`);
      await load();
      openEdit(kind, data);
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const openEdit = (kind, item) => {
    setTopic("");
    const copy = kind === "blog"
      ? { title: item.title || "", slug: item.slug, category: item.category || "Article", excerpt: item.excerpt || "", body: item.body || "", published: !!item.published }
      : { title: item.title || "", slug: item.slug, date_label: item.date_label || "", long: { title: item.long?.title || "", body: item.long?.body || "" }, shorts: (item.shorts || []).map((s) => ({ ...s })), published: !!item.published };
    setEditing({ kind, origSlug: item.slug, item: copy });
  };

  const save = async (silent) => {
    const { kind, origSlug, item } = editing;
    setBusy(true); if (!silent) setMsg("");
    try {
      const payload = kind === "blog"
        ? { title: item.title, new_slug: item.slug, category: item.category, excerpt: item.excerpt, body: item.body, published: item.published }
        : { title: item.title, new_slug: item.slug, date_label: item.date_label, long: item.long, shorts: item.shorts, published: item.published };
      const { data } = await client.put(`/site/admin/${kind === "blog" ? "blog" : "newsletter"}/${origSlug}`, payload);
      if (!silent) { setEditing(null); setMsg("Saved."); }
      await load();
      return data?.slug || origSlug;
    } catch (e) { setMsg(apiErr(e)); throw e; } finally { setBusy(false); }
  };

  const togglePublish = async (kind, item) => {
    setBusy(true); setMsg("");
    try {
      await client.put(`/site/admin/${kind === "blog" ? "blog" : "newsletter"}/${item.slug}`, { title: item.title, published: !item.published });
      await load();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (kind, item) => {    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await client.delete(`/site/admin/${kind === "blog" ? "blog" : "newsletter"}/${item.slug}`); await load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const set = (patch) => setEditing((e) => ({ ...e, item: { ...e.item, ...patch } }));  const setLong = (patch) => setEditing((e) => ({ ...e, item: { ...e.item, long: { ...e.item.long, ...patch } } }));
  const setShort = (i, patch) => setEditing((e) => { const s = [...e.item.shorts]; s[i] = { ...s[i], ...patch }; return { ...e, item: { ...e.item, shorts: s } }; });
  const addShort = () => setEditing((e) => ({ ...e, item: { ...e.item, shorts: [...e.item.shorts, { title: "", body: "" }] } }));
  const rmShort = (i) => setEditing((e) => ({ ...e, item: { ...e.item, shorts: e.item.shorts.filter((_, j) => j !== i) } }));

  const aiWrite = async () => {
    if (!editing) return;
    setAiBusy(true); setMsg("");
    try {
      const { data } = await client.post("/site/admin/ai-draft", { kind: editing.kind, topic });
      const f = data.fields || {};
      if (editing.kind === "blog") {
        set({ title: f.title || editing.item.title, category: f.category || editing.item.category, excerpt: f.excerpt || "", body: f.body || "" });
      } else {
        set({ title: f.title || editing.item.title, long: { title: f.long?.title || "", body: f.long?.body || "" }, shorts: (f.shorts || []).map((s) => ({ title: s.title || "", body: s.body || "" })) });
      }
    } catch (e) { setMsg(apiErr(e)); } finally { setAiBusy(false); }
  };

  const publishSend = async (onlyNew = false) => {
    const label = onlyNew
      ? "Send this issue to subscribers who haven't received it yet?"
      : "Save, publish, and email this issue to all active subscribers (in their language)?";
    if (!window.confirm(label)) return;
    setBusy(true); setMsg("");
    try {
      const effSlug = await save(true);
      const { data } = await client.post(`/site/admin/newsletter/${effSlug}/publish-send${onlyNew ? "?only_new=true" : ""}`);
      setMsg(onlyNew
        ? `Sent to ${data.sent} new subscriber(s); ${data.skipped} already had it.`
        : `Published & sent to ${data.sent}/${data.subscribers} subscriber(s).`);
      await load();
      if (!onlyNew) setEditing(null);
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const previewSend = async () => {
    setBusy(true); setMsg("");
    try {
      const effSlug = await save(true);
      const { data } = await client.post(`/site/admin/newsletter/${effSlug}/preview-send`, { lang: previewLang });
      setMsg(`Preview sent to ${data.to} (${data.lang}). Subscribers not notified.`);
      await load();
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const ListCard = ({ kind, items, icon: Icon, title }) => (
    <Card data-testid={`content-${kind}-card`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon size={18} className="text-[var(--primary)]" />
        <h3 className="font-head text-lg">{title}</h3>
        <span className="text-xs opacity-50">({items.length})</span>
        <Button variant="subtle" className="ms-auto" onClick={() => draftNext(kind)} disabled={busy} data-testid={`draft-next-${kind}`}>
          <FilePlus2 size={15} /> Draft next {kind === "blog" ? "post" : "issue"}
        </Button>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {items.map((it) => (
          <div key={it.slug} data-testid={`content-wrap-${it.slug}`}>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-black/10 dark:border-white/10 px-3 py-2 text-sm" data-testid={`content-row-${it.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{it.title || "(untitled)"}</div>
              <div className="text-xs opacity-55">{kind === "news" ? (it.date_label || "—") : (it.category || "—")} · {it.slug}</div>
              {kind === "news" && it.last_sent_at && (
                <button onClick={() => setExpanded(expanded === it.slug ? null : it.slug)}
                  className="mt-0.5 flex items-center gap-1 text-xs text-[var(--primary)] hover:underline" data-testid={`content-sentlog-${it.slug}`}>
                  <MailCheck size={12} /> Sent to {it.last_sent_count} on {it.last_sent_at.slice(0, 10)}
                  {(it.sends || []).length > 0 && <History size={12} className="ms-1 opacity-70" />}
                </button>
              )}
            </div>
            {it.published ? <Badge color="success">published</Badge> : <Badge color="warning">draft</Badge>}
            <button onClick={() => togglePublish(kind, it)} title={it.published ? "Unpublish" : "Publish"} data-testid={`content-pub-${it.slug}`} className="opacity-70 hover:opacity-100">
              {it.published ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <button onClick={() => openEdit(kind, it)} title="Edit" data-testid={`content-edit-${it.slug}`} className="text-[var(--primary)] hover:opacity-70"><Pencil size={16} /></button>
            <button onClick={() => del(kind, it)} title="Delete" data-testid={`content-del-${it.slug}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={16} /></button>
          </div>
          {expanded === it.slug && (it.sends || []).length > 0 && (
            <div className="ms-3 mt-1 mb-1 rounded-md border-s-2 border-[var(--primary)]/40 bg-black/[0.03] dark:bg-white/[0.05] px-3 py-2 text-xs" data-testid={`content-history-${it.slug}`}>
              <div className="mb-1 font-medium opacity-70">Broadcast history</div>
              {[...it.sends].reverse().map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-2 py-0.5" data-testid={`history-entry-${it.slug}-${i}`}>
                  <span className="opacity-70">{(s.at || "").slice(0, 10)}</span>
                  <span>— sent {s.sent}{s.failed ? `, ${s.failed} failed` : ""}</span>
                  {s.by_language && Object.keys(s.by_language).length > 0 && (
                    <span className="opacity-60">({Object.entries(s.by_language).map(([l, c]) => `${l}:${c}`).join(", ")})</span>
                  )}
                  {s.by && <span className="opacity-50">· by {s.by}</span>}
                </div>
              ))}
            </div>
          )}
          </div>
        ))}
        {items.length === 0 && <p className="py-4 text-center text-sm opacity-50">Nothing yet.</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="content-manager">
      <div className="flex items-center gap-2">
        <h2 className="font-head text-2xl">Content authoring</h2>
        <span className="text-sm opacity-60">Draft, edit, and publish Blog posts &amp; FIDUX™ issues.</span>
      </div>
      {msg && <p className="text-sm opacity-70" data-testid="content-msg">{msg}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        <ListCard kind="news" items={news} icon={Newspaper} title="FIDUX™ issues" />
        <ListCard kind="blog" items={blog} icon={BookOpen} title="Blog posts" />
      </div>

      <Card data-testid="send-back-issues">
        <div className="flex items-center gap-2">
          <Send size={16} className="text-[var(--primary)]" />
          <h3 className="font-head text-lg">Send back issues to a subscriber</h3>
        </div>
        <p className="mt-1 text-sm opacity-60">Email one or more past FIDUX™ editions directly to a subscriber's inbox.</p>
        <div className="mt-3 max-w-md">
          <Input label="Subscriber email" type="email" value={beEmail}
            onChange={(e) => setBeEmail(e.target.value)} data-testid="back-issue-email" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="back-issue-list">
          {news.map((n) => (
            <button key={n.slug} type="button" onClick={() => toggleSlug(n.slug)} data-testid={`back-issue-pick-${n.slug}`}
              className={`rounded-full border px-3 py-1 text-xs transition ${beSlugs.includes(n.slug)
                ? "border-[var(--primary)] bg-[var(--primary)] text-white"
                : "border-black/15 dark:border-white/15 hover:border-[var(--primary)]"}`}>
              {n.date_label ? `${n.date_label} · ` : ""}{n.title.replace("FIDUX™ — ", "")}
            </button>
          ))}
        </div>
        <Button className="mt-3" onClick={sendBackIssues} disabled={beBusy || !beEmail.trim() || beSlugs.length === 0} data-testid="back-issue-send">
          {beBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send {beSlugs.length || ""} to subscriber
        </Button>
      </Card>

      <Modal open={!!editing} onClose={() => !busy && setEditing(null)} wide
        title={editing ? `Edit ${editing.kind === "blog" ? "blog post" : "FIDUX™ issue"}` : ""} testid="content-editor">
        {editing && (
          <div className="space-y-3">
            <Input label="Title" value={editing.item.title} onChange={(e) => set({ title: e.target.value })} data-testid="editor-title" />
            <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-[var(--primary)]/40 bg-[var(--primary)]/[0.04] p-3" data-testid="ai-write-panel">
              <div className="min-w-[200px] flex-1">
                <Input label="AI topic (optional)" placeholder="e.g. protecting a family office from key-person risk"
                  value={topic} onChange={(e) => setTopic(e.target.value)} disabled={aiBusy} data-testid="ai-topic" />
              </div>
              <Button onClick={aiWrite} disabled={aiBusy || busy} data-testid="ai-write-btn">
                {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {aiBusy ? "Writing…" : "Write with AI"}
              </Button>
            </div>
            <p className="text-xs opacity-55">Generates a starting draft in the FopFo house style. Review and edit before publishing.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Slug (URL)" value={editing.item.slug} onChange={(e) => set({ slug: e.target.value })} data-testid="editor-slug" />
              {editing.kind === "blog"
                ? <Input label="Category" value={editing.item.category} onChange={(e) => set({ category: e.target.value })} data-testid="editor-category" />
                : <Input label="Date label (YYYY-MM)" value={editing.item.date_label} onChange={(e) => set({ date_label: e.target.value })} data-testid="editor-date" />}
            </div>

            {editing.kind === "blog" ? (
              <>
                <Field label="Excerpt"><textarea className={ta} rows={2} value={editing.item.excerpt} onChange={(e) => set({ excerpt: e.target.value })} data-testid="editor-excerpt" /></Field>
                <Field label="Body (blank line separates paragraphs)"><textarea className={ta} rows={12} value={editing.item.body} onChange={(e) => set({ body: e.target.value })} data-testid="editor-body" /></Field>
              </>
            ) : (
              <>
                <Input label="Feature title" value={editing.item.long.title} onChange={(e) => setLong({ title: e.target.value })} data-testid="editor-long-title" />
                <Field label="Feature body"><textarea className={ta} rows={10} value={editing.item.long.body} onChange={(e) => setLong({ body: e.target.value })} data-testid="editor-long-body" /></Field>
                <div className="rounded-lg border border-black/10 dark:border-white/10 p-3">
                  <div className="mb-2 flex items-center gap-2"><span className="text-sm font-medium">Short notes</span>
                    <Button variant="subtle" className="ms-auto" onClick={addShort} data-testid="editor-add-short"><Plus size={14} /> Add note</Button>
                  </div>
                  <div className="space-y-3">
                    {editing.item.shorts.map((s, i) => (
                      <div key={i} className="space-y-1.5 rounded-md bg-black/[0.03] dark:bg-white/[0.05] p-2" data-testid={`editor-short-${i}`}>
                        <div className="flex gap-2">
                          <Input label={`Note ${i + 1} title`} value={s.title} onChange={(e) => setShort(i, { title: e.target.value })} data-testid={`editor-short-title-${i}`} />
                          <button onClick={() => rmShort(i)} className="mt-6 text-[#DA1E28] hover:opacity-70" data-testid={`editor-short-del-${i}`}><Trash2 size={16} /></button>
                        </div>
                        <Field label="Body"><textarea className={ta} rows={3} value={s.body} onChange={(e) => setShort(i, { body: e.target.value })} data-testid={`editor-short-body-${i}`} /></Field>
                      </div>
                    ))}
                    {editing.item.shorts.length === 0 && <p className="text-xs opacity-50">No short notes.</p>}
                  </div>
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.item.published} onChange={(e) => set({ published: e.target.checked })} data-testid="editor-published" />
              Published (visible on the public site)
            </label>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
              {editing.kind === "news" && (
                <>
                  <select value={previewLang} onChange={(e) => setPreviewLang(e.target.value)} disabled={busy}
                    className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" data-testid="preview-lang">
                    {LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
                  </select>
                  <Button variant="ghost" onClick={previewSend} disabled={busy || aiBusy || !editing.item.title.trim()} data-testid="editor-preview-send">
                    <MailCheck size={15} /> Preview to me
                  </Button>
                  {news.find((x) => x.slug === editing.origSlug)?.last_sent_at && (
                    <Button variant="ghost" onClick={() => publishSend(true)} disabled={busy || aiBusy} data-testid="editor-resend-new">
                      <Send size={15} /> Resend to new only
                    </Button>
                  )}
                  <Button variant="subtle" onClick={() => publishSend(false)} disabled={busy || aiBusy || !editing.item.title.trim()} data-testid="editor-publish-send">
                    <Send size={15} /> Publish &amp; notify subscribers
                  </Button>
                </>
              )}
              <Button onClick={() => save()} disabled={busy || !editing.item.title.trim()} data-testid="editor-save">Save</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
